import { useEffect, useMemo, useState } from "react";

import { useTran } from "@/hooks";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import AppDialog from "@/components/common/AppDialog";
import { InputField, SelectField } from "@/components/common";

import SurveyQuestionOptionsEditor from "./SurveyQuestionOptionsEditor";
import SurveyQuestionToggles from "./SurveyQuestionToggles";
import {
  OPTION_TYPES,
  QUESTION_TYPES,
  POINTS_ENABLED_TYPES,
  clampPoints,
  defaultOptionsForType,
  sanitizeOptionsForType,
  maxOptionPoints,
} from "../utils/survey-form";

const isChoiceType = (type) => OPTION_TYPES.includes(type);
const isOptionEditableType = (type) =>
  type === "single_choice" || type === "multiple_choice";
const isNumericType = (type) => ["number"].includes(type);

const SurveyQuestionDialog = ({
  open,
  onOpenChange,
  initialQuestion,
  categories = [],
  mode = "standard",
  onSave,
}) => {
  const { t } = useTran();
  const [draft, setDraft] = useState(initialQuestion);

  useEffect(() => {
    if (open) {
      setDraft(initialQuestion);
    }
  }, [open, initialQuestion]);

  const categoryOptions = useMemo(() => {
    const items = categories?.length ? categories : ["common"];
    return items.map((category) => ({
      value: category,
      label: category.charAt(0).toUpperCase() + category.slice(1),
    }));
  }, [categories]);

  const questionTypeOptions = useMemo(
    () =>
      QUESTION_TYPES.map((type) => ({
        value: type,
        label: type.replace(/_/g, " "),
      })),
    [],
  );

  const questionType = draft?.questionType || "text";
  const pointsEnabled = POINTS_ENABLED_TYPES.includes(questionType);
  const allowPointsToggle = mode === "custom" && pointsEnabled;
  const requirePoints =
    pointsEnabled && (draft?.requirePoints !== false || mode !== "custom");

  const updateDraft = (patch) => {
    setDraft((prev) => {
      const next = { ...prev, ...patch };
      if (patch.questionType && patch.questionType !== prev.questionType) {
        const nextType = patch.questionType;
        next.options = sanitizeOptionsForType(nextType, prev.options);
        next.requirePoints = POINTS_ENABLED_TYPES.includes(nextType);
        next.points = POINTS_ENABLED_TYPES.includes(nextType)
          ? clampPoints((prev.points ?? maxOptionPoints(next.options)) || 1)
          : 0;
        if (!isChoiceType(nextType)) {
          next.allowOther = false;
        }
      }
      return next;
    });
  };

  const handleAddOption = () => {
    setDraft((prev) => {
      const options = prev.options || [];
      if (options.length >= 5) return prev;
      const nextIndex = options.length + 1;
      return {
        ...prev,
        options: [
          ...options,
          {
            value: `option${nextIndex}`,
            label: `Option ${nextIndex}`,
            points: 1,
          },
        ],
      };
    });
  };

  const handleOptionChange = (index, patch) => {
    setDraft((prev) => {
      const options = [...(prev.options || [])];
      options[index] = { ...options[index], ...patch };
      return { ...prev, options };
    });
  };

  const handleRemoveOption = (index) => {
    setDraft((prev) => ({
      ...prev,
      options: (prev.options || []).filter((_, idx) => idx !== index),
    }));
  };

  const handleSave = () => {
    const normalizedType = draft.questionType || "text";
    const normalizedOptions = sanitizeOptionsForType(
      normalizedType,
      draft.options || defaultOptionsForType(normalizedType),
    );
    const points = requirePoints
      ? clampPoints((draft.points ?? maxOptionPoints(normalizedOptions)) || 1)
      : 0;

    const cleaned = {
      ...draft,
      questionText: draft.questionText?.trim() || "",
      description: draft.description || "",
      category: draft.category || categories[0] || "common",
      questionType: normalizedType,
      required: Boolean(draft.required),
      options: isChoiceType(normalizedType) ? normalizedOptions : [],
      points,
      requirePoints: requirePoints || false,
      minValue: isNumericType(normalizedType) ? draft.minValue : undefined,
      maxValue: isNumericType(normalizedType) ? draft.maxValue : undefined,
      placeholder:
        normalizedType === "text" || normalizedType === "long_text"
          ? draft.placeholder || ""
          : "",
    };

    onSave?.(cleaned);
  };

  if (!draft) return null;

  return (
    <AppDialog
      open={open}
      onOpenChange={onOpenChange}
      title={draft?.questionText ? t("Edit Question") : t("Add Question")}
      description={t(
        "Configure text, category, type, and points. Applied automatically.",
      )}
      size="lg"
      bodyClassName="space-y-4"
      footer={
        <div className="flex justify-end gap-3">
          <Button variant="outline" onClick={() => onOpenChange?.(false)}>
            {t("Cancel")}
          </Button>
          <Button onClick={handleSave}>{t("Save Question")}</Button>
        </div>
      }
    >
      <div className="space-y-4">
        <InputField
          label={t("Question Text")}
          labelClassName="text-xs font-semibold uppercase tracking-wide text-muted-foreground"
          value={draft.questionText}
          onChange={(event) =>
            updateDraft({ questionText: event.target.value })
          }
          placeholder={t("Enter question")}
        />

        <div className="grid gap-4 md:grid-cols-2">
          <SelectField
            label={t("Category")}
            labelClassName="text-xs font-semibold uppercase tracking-wide text-muted-foreground"
            value={draft.category || categories[0] || "common"}
            onValueChange={(value) => updateDraft({ category: value })}
            options={categoryOptions}
            placeholder={t("Select category")}
          />
          <SelectField
            label={t("Question Type")}
            labelClassName="text-xs font-semibold uppercase tracking-wide text-muted-foreground"
            value={questionType}
            onValueChange={(value) => updateDraft({ questionType: value })}
            options={questionTypeOptions}
            placeholder={t("Select type")}
          />
        </div>

        <div className="space-y-2">
          <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            {t("Description")}
          </label>
          <Textarea
            value={draft.description}
            onChange={(event) =>
              updateDraft({ description: event.target.value })
            }
            placeholder={t("Optional guidance")}
          />
        </div>

        {(questionType === "text" || questionType === "long_text") && (
          <InputField
            label={t("Placeholder")}
            labelClassName="text-xs font-semibold uppercase tracking-wide text-muted-foreground"
            value={draft.placeholder || ""}
            onChange={(event) =>
              updateDraft({ placeholder: event.target.value })
            }
            placeholder={t("Enter placeholder")}
          />
        )}

        {isNumericType(questionType) ? (
          <div className="grid gap-4 md:grid-cols-2">
            <InputField
              label={t("Minimum")}
              labelClassName="text-xs font-semibold uppercase tracking-wide text-muted-foreground"
              type="number"
              value={draft.minValue ?? ""}
              onChange={(event) =>
                updateDraft({
                  minValue:
                    event.target.value === ""
                      ? undefined
                      : Number(event.target.value),
                })
              }
            />
            <InputField
              label={t("Maximum")}
              labelClassName="text-xs font-semibold uppercase tracking-wide text-muted-foreground"
              type="number"
              value={draft.maxValue ?? ""}
              onChange={(event) =>
                updateDraft({
                  maxValue:
                    event.target.value === ""
                      ? undefined
                      : Number(event.target.value),
                })
              }
            />
          </div>
        ) : null}

        <SurveyQuestionToggles
          required={draft.required}
          onRequiredChange={(value) => updateDraft({ required: value })}
          showScoringToggle={allowPointsToggle}
          requirePoints={requirePoints}
          onRequirePointsChange={(value) =>
            updateDraft({ requirePoints: value })
          }
        />

        {isChoiceType(questionType) ? (
          <SurveyQuestionOptionsEditor
            options={draft.options}
            pointsEnabled={pointsEnabled}
            requirePoints={requirePoints}
            isEditable={isOptionEditableType(questionType)}
            allowOther={draft.allowOther}
            onAddOption={handleAddOption}
            onOptionLabelChange={(index, value) =>
              handleOptionChange(index, { label: value, value })
            }
            onOptionPointsChange={(index, value) =>
              handleOptionChange(index, { points: clampPoints(value || 1) })
            }
            onRemoveOption={handleRemoveOption}
            onAllowOtherChange={(value) => updateDraft({ allowOther: value })}
          />
        ) : null}
      </div>
    </AppDialog>
  );
};

export default SurveyQuestionDialog;
